import { Box, Button, Container } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";

import { getConfig } from "config/config";
import { Typography } from "new-components/typography/Typography";

const config = getConfig();

const { JANZU_URL } = config;

const DJANGO_ADMIN_QUOTE_CHARS = new Set(":/_#?;@&=+$,\"[]<>%\\\n".split(""));

function djangoAdminQuote(s: string): string {
  return s
    .split("")
    .map((c) =>
      DJANGO_ADMIN_QUOTE_CHARS.has(c)
        ? `_${c.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")}`
        : c,
    )
    .join("");
}

export function DjangoAdminHelpPage() {
  const { enqueueSnackbar } = useSnackbar();

  return (
    <Container>
      <Box className="flex flex-col gap-4 pt-4">
        <Box className="flex flex-col gap-2 border border-gray-300 rounded-lg p-3 shadow-md bg-white">
          <Typography variant="subtitle" fontWeight="bold" className="mb-2">
            Django DB Admin
          </Typography>
          <Typography>
            The Django Admin site gives you raw access to most of the database tables.
          </Typography>
          <Button
            sx={{ width: "fit-content" }}
            onClick={() => { window.location.href = `${JANZU_URL}admin/` }}
            variant="contained"
          >
            Django DB Admin
          </Button>
        </Box>

        <Box className="flex flex-col gap-2 border border-gray-300 rounded-lg p-3 shadow-md bg-white">
          <Typography variant="subtitle" fontWeight="bold" className="mb-2">
            Custom Admin Functions
          </Typography>
          <Typography>
            For various administrative functions like provisioning Vision Cores or adjusting user accounts.
          </Typography>
          <Button
            sx={{ width: "fit-content" }}
            onClick={() => { window.location.href = `${JANZU_URL}admin/functions/` }}
            variant="contained"
          >
            Custom Admin Functions
          </Button>
        </Box>

        <Box className="flex flex-col gap-2 border border-gray-300 rounded-lg p-3 shadow-md bg-white">
          <Typography variant="subtitle" fontWeight="bold" className="mb-2">
            User Impersonation
          </Typography>
          <Typography>
            If you need to view the site as a customer, go to the User Profile table and select the &quot;Hijack&quot;
            button on the right side. (Sorry for the horrible name on the button, but that is the name of the standard
            library for this.)
          </Typography>
          <Button
            sx={{ width: "fit-content" }}
            onClick={() => { window.location.href = `${JANZU_URL}admin/authz/userprofile/` }}
            variant="contained"
          >
            User Profile table
          </Button>
          <Typography>
            You can switch back by 1) logging out, or 2) by visiting the django admin page (above) which will prompt you
            that you are using an assumed identity and selecting the option Release.
          </Typography>
        </Box>

        <Box className="flex flex-col gap-2 border border-gray-300 rounded-lg p-3 shadow-md bg-white">
          <Typography variant="subtitle" fontWeight="bold" className="mb-2">
            Investigating IQ and Detector issues
          </Typography>
          <Typography>
            If you need to investigate an issue with an IQ or Detector, you can use the following functions to help.
          </Typography>

          <Box className="flex flex-col gap-4">
            <Box>
              <Typography variant="subtitle" fontWeight="bold" className="mb-2">
                Investigate Detector by id
              </Typography>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                }}
                className="flex flex-col gap-2"
              >
                <input
                  type="text"
                  name="detector_id"
                  placeholder="Enter detector ID"
                  className="p-2 border rounded"
                  required
                />
                <Box className="flex gap-2">
                  <Button
                    onClick={(e) => {
                      const form = (e.target as HTMLElement).closest("form");
                      const detectorId = new FormData(form as HTMLFormElement).get("detector_id");
                      window.location.href = `${JANZU_URL}admin/apparati/predictor/${djangoAdminQuote(String(detectorId))}/change/`;
                    }}
                    variant="contained"
                  >
                    Detector DB Entry
                  </Button>
                  <Button
                    onClick={(e) => {
                      const form = (e.target as HTMLElement).closest("form");
                      const detectorId = new FormData(form as HTMLFormElement).get("detector_id");
                      window.location.href = `${JANZU_URL}reef/detectors/${detectorId}`;
                    }}
                    variant="contained"
                  >
                    Detector Detail Page
                  </Button>
                </Box>
              </form>
            </Box>

            <Box>
              <Typography variant="subtitle" fontWeight="bold" className="mb-2">
                Investigate Image Query by id
              </Typography>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                }}
                className="flex flex-col gap-2"
              >
                <input
                  type="text"
                  name="iqId"
                  placeholder="Enter image query ID"
                  className="p-2 border rounded"
                  required
                />
                <Box className="flex gap-2">
                  <Button
                    onClick={(e) => {
                      const form = (e.target as HTMLElement).closest("form");
                      const iqId = new FormData(form as HTMLFormElement).get("iqId");
                      window.location.href = `${JANZU_URL}admin/apparati/posicheck/${djangoAdminQuote(String(iqId))}/change/`;
                    }}
                    variant="contained"
                  >
                    IQ DB Entry
                  </Button>
                  <Button
                    onClick={async (e) => {
                      const form = (e.target as HTMLElement).closest("form");
                      const iqId = new FormData(form as HTMLFormElement).get("iqId");
                      try {
                        const response = await fetch(`${JANZU_URL}device-api/v1/image-queries/${iqId}`);
                        const data = await response.json();
                        const detectorId = data.detector_id;
                        // Construct the data page filter.
                        const filterModel = encodeURIComponent(JSON.stringify({
                          items: [{
                            value: iqId,
                            columnField: "iq_id",
                            operatorValue: "="
                          }]
                        }));
                        window.location.href = `/reef/data/image-queries?detector_id=${detectorId}&filterModel=${filterModel}`;
                      } catch (error) {
                        enqueueSnackbar("Error fetching image query details. Please try again.", {
                          variant: "error",
                          autoHideDuration: 5000
                        });
                      }
                    }}
                    variant="contained"
                  >
                    Reef Data Page
                  </Button>
                </Box>
              </form>
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
